import React from "react";
import usePermissions from "../permissions/usePermissions";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PermissionNameType } from "@mm-products/java-multima-framework";
import { toast } from "react-toastify";
import { toastStyles } from "../TaxGuruStyles";

export type ProtectedRouteOptions = {
    linksOnFail?: Array<{ link: string; permissions: Array<PermissionNameType> }>;
    noErrorToast?: boolean;
};
export type ProtectedRouteProps = {
    permissions: Array<PermissionNameType>;
    children: React.ReactNode;
    permissionsCheck?: "all-required" | "at-least-one";
    options?: ProtectedRouteOptions;
};

const ProtectedRoute = ({ permissions, children, options, permissionsCheck = "all-required" }: ProtectedRouteProps) => {
    const { hasAllPermissions, hasAtLeastOnePermission, accountPermissions } = usePermissions();
    const location = useLocation();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [authorized, setAuthorized] = React.useState<boolean>();

    React.useEffect(() => {
        function isAuthroized(permissions: Array<string>) {
            if (permissionsCheck === "all-required") {
                return hasAllPermissions(permissions);
            } else {
                return hasAtLeastOnePermission(permissions);
            }
        }

        const authorized = isAuthroized(permissions);
        if (!authorized) {
            if (options?.linksOnFail) {
                for (const linkOnFail of options.linksOnFail) {
                    if (isAuthroized(linkOnFail.permissions)) {
                        navigate(linkOnFail.link);
                        break;
                    }
                }
            } else {
                toast.error(t("navigation.urlForbidden", { url: location.pathname }).toString(), { style: toastStyles.error });
                navigate("/");
            }
        }
        setAuthorized(authorized);
    }, [accountPermissions, hasAllPermissions, hasAtLeastOnePermission, location, navigate, options?.linksOnFail, permissions, permissionsCheck, t]);

    if (authorized === undefined) {
        return null;
    } else if (authorized) {
        return <>{children}</>;
    } else {
        return null;
    }
};

export default ProtectedRoute;
